import { Paper } from '../models/result-set';

export type ExportFormat = 'json' | 'markdown' | 'bibtex';

const convertPapersToJson = (papers: Paper[], _: string): string => {
  const formattedPapers = papers.map((paper) => ({
    title: paper.title,
    authors: paper.authors,
    year: paper.year,
    venue: paper.venue,
    score: paper.relevanceScore,
    abstract: paper.abstract,
    corpusId: paper.corpusId,
    url: paper.url,
  }));

  const result = {
    papers: formattedPapers,
  };

  return JSON.stringify(result, null, 2);
};

const convertPapersToMarkdown = (papers: Paper[], _: string): string => {
  return (
    `# Papers\n\n` +
    papers
      .map((paper) => {
        // [\s\n]+ removes all spaces (\s) and newlines (\n) from the start of the string to ensure it Markdown-supported
        const cleanedAbstract =
          paper.abstract && paper.abstract.replace(/^[\s\n]+/, '');

        return `- **${paper.title}**\n   ${paper.authors
          .map((author) => author.name)
          .join(', ')}\n   ${paper.venue} ${paper.year}\n   [open paper page](${
          paper.url
        })\n   <details>\n     <summary> Abstract </summary>\n     ${cleanedAbstract}\n  </details>\n`;
      })
      .join('\n')
  );
};

export const convertPapersToBibTex = (papers: Paper[]): string => {
  return papers
    .map((paper, index) => formatBibTeX(paper, indexToABC(index)))
    .join('\n\n');
};

const indexToABC = (index: number): string => {
  let result = '';
  while (index >= 0) {
    result = String.fromCharCode(65 + (index % 26)) + result;
    index = Math.floor(index / 26) - 1;
  }
  return result;
};

export const formattingPapers = (
  papers: Paper[],
  query: string,
  formatType: ExportFormat,
): string => {
  let formattedContent = '';
  switch (formatType) {
    case 'json':
      formattedContent = convertPapersToJson(papers, query);
      break;
    case 'markdown':
      formattedContent = convertPapersToMarkdown(papers, query);
      break;
    case 'bibtex':
      formattedContent = convertPapersToBibTex(papers);
      break;
  }
  return formattedContent;
};

const formatBibTeX = (paper: Paper, suffix: string): string => {
  const hasJournal = paper.journal && paper.journal.name;
  const entryType = hasJournal ? 'article' : 'inproceedings';

  // Get first author
  const firstAuthor = paper.authors.length > 0 && paper.authors[0];

  // Extract first three words from the title (if available)
  const PatternNonWordCharacters = /\W/g;
  const PatternOneOrMoreSpaces = /\s/;
  const titleWords = paper.title.split(PatternOneOrMoreSpaces) || [];
  const abbreviatedTitle = [
    titleWords.shift(),
    titleWords.shift(),
    titleWords.shift(),
  ]
    .filter((str) => !!str)
    // @ts-expect-error -- the filter call above ensures str is not undefined
    .map((str, i) => (i > 0 ? str.substr(0, 1).toUpperCase() : str))
    .join('');

  // Extract publication year
  const publicationYear = extractYear(paper);

  // Build citation key
  const citationKeyParts = [publicationYear || '', abbreviatedTitle];
  if (firstAuthor) {
    const words = firstAuthor.name.split(' ').filter((a) => a.length > 0);
    // if there is a last name get it, else get the first name
    const name = words.length > 1 ? words[words.length - 1] : words[0];
    citationKeyParts.unshift(name);
  }
  const citationKey =
    citationKeyParts
      .map((part) => part.replace(PatternNonWordCharacters, ''))
      .join('')
      .substring(0, 45 - suffix.length) + suffix;

  // Format authors
  const formattedAuthors = paper.authors
    .map((author) => author.name)
    .join(' and ');

  // Format BibTeX fields
  const journalField = hasJournal
    ? `journal={${encodeBibTexComponent(paper.journal?.name || '')}}`
    : paper.venue
    ? `booktitle={${encodeBibTexComponent(paper.venue)}}`
    : undefined;
  const yearField = publicationYear
    ? `year={${encodeBibTexComponent(publicationYear)}}`
    : undefined;
  const volumeField =
    hasJournal && paper.journal?.volume
      ? `volume={${encodeBibTexComponent(paper.journal.volume)}}`
      : undefined;
  const pagesField =
    hasJournal && paper.journal?.pages
      ? `pages={${encodeBibTexComponent(paper.journal.pages)}}`
      : undefined;
  const urlField = paper.url
    ? `url={${encodeBibTexComponent(paper.url)}}`
    : undefined;

  // Combine fields
  const bibTeXFields = [
    `title={${encodeBibTexComponent(paper.title)}}`,
    `author={${encodeBibTexComponent(formattedAuthors)}}`,
    journalField,
    yearField,
    volumeField,
    pagesField,
    urlField,
  ]
    .filter((field) => !!field)
    .join(',\n  ');

  return `@${entryType}{${encodeBibTexComponent(
    citationKey,
  )},\n  ${bibTeXFields}\n}`;
};

function extractYear(paper: Paper): string | null {
  const extractedYear = paper.pubDate?.split('-')[0];
  return extractedYear || paper.year.toString() || null;
}

// taken from s2 citation bibtex temp-citation-util.ts
const BibTexSpecialChars = [
  ['{', '\\{'],
  ['}', '\\}'],
  ['$', '\\$'],
  ['&', '\\&'],
  ['%', '\\%'],
  ['#', '\\#'],
  ['_', '\\_'],
  ['Ä', '{\\"A}'],
  ['ä', '{\\"a}'],
  ['Ë', '{\\"E}'],
  ['ë', '{\\"e}'],
  ['Ï', '{\\"I}'],
  ['ï', '{\\"i}'],
  ['Ö', '{\\"O}'],
  ['ö', '{\\"o}'],
  ['Ü', '{\\"U}'],
  ['ü', '{\\"u}'],
  ['Ÿ', '{\\"Y}'],
  ['ÿ', '{\\"y}'],
  ['ß', '{\\ss}'],
  ['Ø', '{\\O}'],
  ['ø', '{\\o}'],
  ['Á', "{\\'A}"],
  ['á', "{\\'a}"],
  ['Ć', "{\\'C}"],
  ['ć', "{\\'c}"],
  ['É', "{\\'E}"],
  ['é', "{\\'e}"],
  ['Í', "{\\'I}"],
  ['í', "{\\'i}"],
  ['Ó', "{\\'O}"],
  ['ó', "{\\'o}"],
  ['Ú', "{\\'U}"],
  ['ú', "{\\'u}"],
  ['Ý', "{\\'Y}"],
  ['ý', "{\\'y}"],
  ['À', '{\\`A}'],
  ['à', '{\\`a}'],
  ['È', '{\\`E}'],
  ['è', '{\\`e}'],
  ['Ì', '{\\`I}'],
  ['ì', '{\\`i}'],
  ['Ò', '{\\`O}'],
  ['ò', '{\\`o}'],
  ['Ù', '{\\`U}'],
  ['ù', '{\\`u}'],
  ['Â', '{\\^A}'],
  ['â', '{\\^a}'],
  ['Ê', '{\\^E}'],
  ['ê', '{\\^e}'],
  ['Î', '{\\^I}'],
  ['î', '{\\^i}'],
  ['Ô', '{\\^O}'],
  ['ô', '{\\^o}'],
  ['Û', '{\\^u}'],
  ['û', '{\\^u}'],
  ['Ã', '{\\~A}'],
  ['ã', '{\\~a}'],
  ['Ñ', '{\\~N}'],
  ['ñ', '{\\~n}'],
  ['Õ', '{\\~O}'],
  ['õ', '{\\~o}'],
  ['Æ', '{\\aE}'],
  ['æ', '{\\ae}'],
  ['Œ', '{\\OE}'],
  ['œ', '{\\oe}'],
  ['Č', '{\\vC}'],
  ['č', '{\\vc}'],
  ['Š', '{\\vS}'],
  ['š', '{\\vs}'],
  ['ž', '{\\vz}'],
  ['Å', '{\\AA}'],
  ['å', '{\\aa}'],
  ['™', '{\\texttrademark}'],
  ['®', '{\\textregistered}'],
  ['©', '{\\textcopyright}'],
  ['α', '$\\alpha$'],
  ['β', '$\\beta$'],
  ['γ', '$\\gamma$'],
  ['Γ', '$\\Gamma$'],
  ['δ', '$\\delta$'],
  ['Δ', '$\\Delta$'],
  ['ε', '$\\epsilon$'],
  ['ζ', '$\\zeta$'],
  ['η', '$\\eta$'],
  ['θ', '$\\theta$'],
  ['Θ', '$\\Theta$'],
  ['ι', '$\\iota$'],
  ['κ', '$\\kappa$'],
  ['λ', '$\\lambda$'],
  ['Λ', '$\\Lambda$'],
  ['μ', '$\\mu$'],
  ['ν', '$\\nu$'],
  ['ξ', '$\\xi$'],
  ['ο', '$\\omicron$'],
  ['π', '$\\pi$'],
  ['Π', '$\\Pi$'],
  ['ρ', '$\\rho$'],
  ['σ', '$\\sigma$'],
  ['Σ', '$\\Sigma$'],
  ['τ', '$\\tau$'],
  ['υ', '$\\upsilon$'],
  ['Υ', '$\\Upsilon$'],
  ['φ', '$\\phi$'],
  ['Φ', '$\\Phi$'],
  ['χ', '$\\chi$'],
  ['ψ', '$\\psi$'],
  ['Ψ', '$\\Psi$'],
  ['ω', '$\\omega$'],
  ['Ω', '$\\Omega$'],
] as const;

function encodeBibTexComponent(str: string): string {
  return BibTexSpecialChars.reduce((curStr, [character, replacement]) => {
    return curStr.replace(new RegExp(`[${character}]`, 'g'), replacement);
  }, str + '');
}
